import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { DatePicker, Label } from '@/shared/components';
import {
    REGISTRATION_DATE_REQUIRED,
    REGISTRATION_DATE_UNREADABLE,
    type AdditionalVehicleDetailsFormInputs,
} from '../../schemas/additionalvehicledetails.schema';

interface RegistrationDateFieldProps {
    control: Control<AdditionalVehicleDetailsFormInputs>;
    fieldIssues: Partial<Record<'registrationDate', string>>;
}

export const RegistrationDateField: React.FC<RegistrationDateFieldProps> = ({ control, fieldIssues }) => {
    return (
        <div>
            <Label htmlFor="registrationDate" required>First Registered Date</Label>
            <Controller
                name="registrationDate"
                control={control}
                render={({ field, fieldState: { error } }) => {
                    // Issue #807. The message is driven by the field VALUE, not only by
                    // fieldState.error, and that is deliberate. With autoSubmit={true}
                    // (VehicleSelectionStep.tsx:127) this form renders NO submit button
                    // (see the `!autoSubmit` button in `AdditionalVehicleDetailsForm.tsx`),
                    // and RHF's mount-time validity pass sets isValid WITHOUT
                    // populating errors -- so for an untouched empty field `error` is
                    // undefined. Driving the copy from `error?.message` alone would give a
                    // form that silently stops auto-saving with nothing on screen to say why:
                    // worse than the 422 this replaces.
                    const missing = !field.value; // covers undefined AND the null
                                                   // DatePicker emits on clear
                    // Issue #859 D2 row 3 / D3 part 2 — an unparseable stored date
                    // string becomes `new Date(<string>)`, a real Date instance
                    // that is truthy, so `missing` above misses it. Zod rejects it
                    // with `invalid_date` at the schema level, but #807's message
                    // is deliberately value-driven (see above), so this needs its
                    // own value-driven branch and its own copy rather than relying
                    // on fieldIssues.registrationDate.
                    const unreadable = field.value instanceof Date && Number.isNaN(field.value.getTime());
                    // Issue #859 review follow-up — `fieldIssues.registrationDate` was never
                    // consulted, unlike every other field. Added as a final fallback, not a
                    // replacement: `missing`/`unreadable` above cover every issue the schema can
                    // raise for this field today, so this fallback is presently unreachable and
                    // exists only so a FUTURE schema constraint on registrationDate cannot regress
                    // to #859's silent-block bug the way it did before this fix existed. It does
                    // not touch the role logic below, which is driven by `error` (RHF's
                    // post-interaction state), not by which branch produced `message`.
                    const message =
                        error?.message ??
                        (missing ? REGISTRATION_DATE_REQUIRED : unreadable ? REGISTRATION_DATE_UNREADABLE : undefined) ??
                        fieldIssues.registrationDate;
                    const describedBy = message ? 'registrationDate-message' : undefined;

                    return (
                        <>
                            <DatePicker
                                id="registrationDate"
                                aria-invalid={missing || unreadable || !!error}
                                aria-describedby={describedBy}
                                onChange={(date) => {
                                    field.onChange(date);
                                }}
                                error={missing || unreadable || !!error}
                                defaultDate={field.value}
                                placeholder="Select first registered date"
                            />
                            {message && (
                                // role="alert" ONLY for a real post-interaction RHF error,
                                // never for the resting required state. A live region
                                // announces to assistive tech the instant it appears in the
                                // DOM; firing that on MOUNT, before the user has touched
                                // anything, would interrupt for a field nobody has failed to
                                // fill in -- they simply have not got there yet.
                                // aria-invalid (unconditional, above) and aria-describedby
                                // (present whenever `message` renders -- which for this field
                                // is effectively always, since `missing`/`unreadable` cover
                                // every resting state; it is NOT unconditional the way
                                // aria-invalid is) already expose the resting state to a
                                // screen-reader user on focus, which is enough without an
                                // active announcement. Once a real post-interaction error
                                // exists, role="alert" is warranted: the user acted and the
                                // result should interrupt.
                                //
                                // NOT the same shape as
                                // features/quote/components/CollectionLeadInput.tsx:225-229
                                // or DepositInput.tsx:160-168 -- both those role="alert" tags
                                // are unconditional, but their message node only exists once
                                // hasError is true, so there is no resting announcement to
                                // suppress: blank is valid for those fields. registrationDate
                                // has no such resting state -- blank is invalid by design
                                // (#807) -- so this message node, and the aria-invalid it
                                // pairs with, are present from mount. Issue #859 review
                                // follow-up: registrationDate is no longer the only field in
                                // www/ setting aria-invalid="true" on a pristine, never-touched
                                // control -- vin, vrm, lastMotDate and lastServiceDate all do
                                // the same (`VehicleIdentifierFields.tsx`, `ServiceHistoryDateFields.tsx`),
                                // driven by their `fieldIssues` message at
                                // rest. registrationDate stays the one field where that
                                // resting-invalid state is true BY DESIGN (blank is never
                                // valid here, #807) rather than contingent on whether the
                                // seeded value happens to be bad. aria-invalid is truthfully
                                // invalid the instant it renders, and the conditional role is
                                // what keeps that truthful state from also being a live-region
                                // interruption.
                                <p
                                    id="registrationDate-message"
                                    role={error ? 'alert' : undefined}
                                    className="mt-1.5 text-xs text-mp-error"
                                >
                                    {message}
                                </p>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
};
